import { SubmissionStatus } from '@bp/bp-graphql-types';
import { ArrowRightIcon, CheckIcon, Table, TableColumns } from '@bp/ui-components';
import classNames from 'classnames';
import { useRequireParams } from 'hooks/useRequireParams';
import { Room } from 'matrix-js-sdk';
import { useTranslation } from 'react-i18next';
import { isAfter, niceDate } from 'utils/dateCalculations';
import { Submission, useSubmissions } from '../hooks/useSubmissions';
import styles from './SubmissionsPreviewTable.module.scss';

export type SubmissionsPreviewTableCallback = { submissionUuid: string; newMessage: boolean; room: Room | null };

type SubmissionsPreviewTableProps = {
  type: 'todo' | 'new' | 'done';
  onRowClick: ({ submissionUuid, newMessage, room }: SubmissionsPreviewTableCallback) => void;
};

export const SubmissionsPreviewTable = ({ type, onRowClick }: SubmissionsPreviewTableProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const { submissions, hasNewMessage } = useSubmissions({ courseUuid, assignmentUuid });

  const data: Submission[] = submissions.filter((s) => {
    if (type === 'todo') {
      return (
        s.status === SubmissionStatus.Todo ||
        s.status === SubmissionStatus.Draft ||
        s.status === SubmissionStatus.Feedback
      );
    } else if (type === 'new') {
      return s.status === SubmissionStatus.New || s.status === SubmissionStatus.Updated;
    } else if (type === 'done') {
      return s.status === SubmissionStatus.Done;
    } else {
      return false;
    }
  });

  const columns: TableColumns<Submission>[] = [
    {
      id: 'name',
      size: 350,
      canExpand: true,
      cell: ({ row }) => {
        const newMessage = hasNewMessage(row.original.uuid);

        return (
          <div className={styles['info-wrapper']}>
            <div className={styles.info}>
              <div className={styles.name}>
                {type === 'done' && <CheckIcon className={styles.check} />}
                {row.original.owner.name}
              </div>
              {type === 'new' && <div className={styles.date}>{niceDate(row.original.updated, 'short', 'short')}</div>}
              {type === 'done' && (
                <div className={styles.date}>
                  {niceDate(row.original.updated, 'short', 'short')}
                  {isAfter(row.original.updated, row.original.assignment.dueDate) && (
                    <div className={styles.late}>{t('submissions.late')}</div>
                  )}
                </div>
              )}
            </div>
            {newMessage && <div className='bp__dot'></div>}
          </div>
        );
      },
    },
  ];

  return (
    <Table<Submission>
      columns={columns}
      data={data}
      hideHeader
      className={classNames(styles['submissions-preview-table'], 'hide-gutter')}
      isDesktop
      customRowHeight={type === 'todo' ? 'var(--spacing-8)' : 'var(--spacing-10)'}
      customPadding='var(--spacing-6)'
      showRowHover
      onRowClick={(_e, { original }) =>
        onRowClick({ submissionUuid: original.uuid, newMessage: original.newMessage, room: original.room })
      }
      lastColWidth='var(--spacing-6)'
      lastCol={() => <ArrowRightIcon />}
      emptyStateSettings={{
        size: 'small',
        hideIcon: true,
        title: '',
        subtitle:
          type === 'new'
            ? t('submissions.noPending')
            : type === 'todo'
              ? t('submissions.noSubmissions')
              : t('submissions.noTicked'),
        forcedHeight: '100px',
      }}
    />
  );
};
